import {
	FC, useMemo, CSSProperties, useCallback,
} from 'react';
import {
	Row, Col, Dropdown, Menu, Button,
} from 'antd';
import Title from 'antd/lib/typography/Title';
import { DownOutlined } from '@ant-design/icons';

export interface IDropdownOptions {
	id: string;
	label: string;
}

interface IDropdownWithTitleProps {
	description: string;
	title: string;
	dropdownLabel: string;
	options: IDropdownOptions[];
	onSelect: (id: string) => void;
	style?: CSSProperties;
}

/**
 * [Presentational] Dropdown component with a title
 * @param {string} description description for the dropdown
 * @param {string} title title for the dropdown
 * @param {string} dropdownLabel label for the dropdown button
 * @param {IDropdownOptions[]} options the options to be displayed in the dropdown
 * @param {function} onSelect called when a menu item is selected
 * @param {CSSProperties} style additional styling for the dropdown section
 */
const DropdownWithTitle: FC<IDropdownWithTitleProps> = (props) => {
	const {
		description,
		title,
		dropdownLabel,
		options,
		onSelect,
		style,
	} = props;

	const onSelectItem = useCallback(({ key }: { key: string | number }) => onSelect(key.toString()), [onSelect]);

	// Note: extracting this to a separate component causes styling issues
	const dropdownMenu = useMemo(() => (
		<Menu onClick={onSelectItem}>
			{options.map((option) => (
				<Menu.Item key={option.id}>
					{option.label}
				</Menu.Item>
			))}
		</Menu>
	), [options, onSelectItem]);

	return (
		<span>
			<Row align="middle" style={style}>
				<Col style={{ marginRight: '24px' }}>
					<Title style={{ margin: 0, fontSize: 18 }}>{description}</Title>
				</Col>
			</Row>
			<br/>
			<Row align="middle" style={style}>
				<Col style={{ marginRight: '24px' }}>
					<Title style={{ margin: 0, fontSize: 22 }}>{title}</Title>
				</Col>
				<Col>
					<Dropdown trigger={['click']} overlay={dropdownMenu}>
						<Button style={{
							minWidth: '250px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
						}}
						>
							{dropdownLabel}
							<DownOutlined />
						</Button>
					</Dropdown>
				</Col>
			</Row>
		</span>
	);
};

export default DropdownWithTitle;

interface ITeamMemberDropdownWithTitleProps {
	selectedMemberName?: string;
	options: IDropdownOptions[];
	onSelect: (id: string) => void;
}

/**
 * HOC that wraps around DropdownWithTitles to give it team member settings
 */
const withTeamMemberSettings = <T extends ITeamMemberDropdownWithTitleProps>(Component: FC<Omit<T, 'selectedMemberName'> & IDropdownWithTitleProps>) => (props: T) => {
	const { selectedMemberName } = props;
	return (
		<Component
			{...props}
			description='Search lists are used to help find your ideal target audience. Create a search URL on LinkedIn Sales Navigator to capture prospects for this campaign.'
			title="Select team member to view"
			dropdownLabel={selectedMemberName ?? 'Team Members'}
			style={{ marginBottom: selectedMemberName ? '22px' : undefined }}
		/>
	);
};

export const TeamMemberDropdownWithTitle = withTeamMemberSettings(DropdownWithTitle);
